import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import EdayPage from './pages/EdayPage';
import IncidentDashboard from './pages/IncidentDashboard';
import CategoryIncidents from './pages/CategoryIncidents';
import Reports from './pages/Reports';
import ObserverChecklist from './pages/ObserverChecklist';
import Incidents from './pages/Incidents';
import ComplianceDashboard from './pages/ComplianceDashboard';
import IncidentsAction from './pages/IncidentsAction';
import HubActions from './pages/HubActions';
import IncidentsIntervention from './pages/IncidentsIntervention';

import Users from './pages/Users';
import Organizations from './pages/Organizations';
import Roles from './pages/Roles';
import Modules from './pages/Modules';
import Permissions from './pages/Permissions';
import SMS from './pages/SMS';
import Settings from './pages/Settings';
import Debug from './pages/Debug';
import LocationsImport from './pages/LocationsImport';
import { useAutoLogout } from './hooks/useAutoLogout';
import NetworkStatusIndicator from './components/NetworkStatusIndicator';

// Dev-only debug utils
if (process.env.NODE_ENV === 'development') {
  import('./utils/debug').then(({ debugUtils }) => {
    (window as any).debugUtils = debugUtils;
    console.log('🔧 Debug utilities loaded. Use debugUtils.runFullDiagnostics() to troubleshoot login issues.');
  });
}

/** Inner app that has access to AuthContext */
function AppInner() {
  const { user, loading } = useAuth();

  // Auto-logout timer / session handling should live inside the provider
  useAutoLogout();

  // Optional: nicer splash while we hydrate /auth/me
  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-gray-50">
        <div className="flex items-center space-x-3 text-gray-600">
          <div className="w-5 h-5 border-2 border-gray-300 rounded-full animate-spin border-t-transparent" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  // 👇 This key forces a full remount on user switch
  return (
    <div key={user?.id || 'anon'} className="min-h-screen bg-gray-50">
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        {process.env.NODE_ENV === 'development' && (
          <Route path="/debug-login" element={<Debug />} />
        )}

        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              {/* You can also add key here if you prefer the admin area to remount specifically */}
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/eday-dashboard" element={<EdayPage />} />
                  <Route path="/incidents-dashboard" element={<IncidentDashboard />} />
                  <Route path="/incidents/category/:category" element={<CategoryIncidents />} />
                  <Route 
                    path="/compliance-dashboard" 
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <ComplianceDashboard />
                      </ProtectedRoute>
                    } 
                  />

                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute roles={['admin', 'coordinator']}>
                        <Reports />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/observer-checklist"
                    element={
                      <ProtectedRoute roles={['admin', 'coordinator']}>
                        <ObserverChecklist />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/incidents"
                    element={
                      <ProtectedRoute roles={['admin', 'coordinator']}>
                        <Incidents />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/incidents-action"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <IncidentsAction />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/hub-actions"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <HubActions />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/incidents-intervention"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <IncidentsIntervention />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <Users />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/organizations"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <Organizations />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/roles"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <Roles />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/modules"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <Modules />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/permissions"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <Permissions />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/sms"
                    element={
                      <ProtectedRoute roles={['admin', 'coordinator']}>
                        <SMS />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/locations-import"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <LocationsImport />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute roles={['admin']}>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />

                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/debug" element={<Debug />} />
                  )}
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <NetworkStatusIndicator />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
